import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { addDays } from "@/lib/equipment/availability";
import { lineLabel } from "@/lib/equipment/booking-draft";
import { cn } from "@/lib/utils";
import type { DBEquipmentBookingLine } from "@/lib/db-types";

interface EquipmentCalendarProps {
  lines: DBEquipmentBookingLine[];
  today: string;
  onDayClick: (day: string) => void;
  onBookingClick: (bookingId: string) => void;
}

const WEEKDAYS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const monthTitle = (month: string) =>
  new Date(`${month}-01T12:00:00Z`).toLocaleDateString("nb-NO", { month: "long", year: "numeric", timeZone: "UTC" });

/** Seks uker fra mandagen før den første i måneden. */
function monthGrid(month: string): string[] {
  const first = `${month}-01`;
  const weekday = (new Date(`${first}T12:00:00Z`).getUTCDay() + 6) % 7;
  const start = addDays(first, -weekday);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

/** Månedskalender på utstyrskortet (mockup B) med bookingene til utstyret. */
export function EquipmentCalendar({ lines, today, onDayClick, onBookingClick }: EquipmentCalendarProps) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const days = useMemo(() => monthGrid(month), [month]);
  const title = monthTitle(month);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base capitalize">{title}</CardTitle>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Forrige måned">
            <SrIcon name="chevron-left" className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setMonth(today.slice(0, 7))}>I dag</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Neste måned">
            <SrIcon name="chevron-right" className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-px rounded-md border bg-border text-xs" role="grid" aria-label={`Bookinger ${title}`}>
          {WEEKDAYS.map((d) => (
            <div key={d} className="bg-muted/50 px-1.5 py-1 font-medium text-muted-foreground" role="columnheader">{d}</div>
          ))}
          {days.map((day) => {
            const booked = lines.filter((l) => l.date_from <= day && l.date_to >= day);
            const inMonth = day.startsWith(month);
            return (
              <div
                key={day}
                role="gridcell"
                className={cn("min-h-[64px] bg-background p-1 flex flex-col gap-0.5", !inMonth && "bg-muted/30 text-muted-foreground")}
              >
                <button
                  type="button"
                  onClick={() => onDayClick(day)}
                  aria-label={`Book fra ${day.split("-").reverse().join(".")}`}
                  className={cn("self-start rounded px-1 tabular-nums hover:bg-muted", day === today && "bg-primary text-primary-foreground hover:bg-primary")}
                >
                  {Number(day.slice(8))}
                </button>
                {booked.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => onBookingClick(l.booking_id)}
                    title={`${lineLabel(l)}${l.quantity > 1 ? ` · ${l.quantity} stk` : ""}${l.note ? ` · ${l.note}` : ""}`}
                    className="truncate rounded bg-primary/10 px-1 text-left text-[11px] text-primary hover:bg-primary/20"
                  >
                    {lineLabel(l)}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Klikk på en dag for å booke fra den datoen. Klikk på en booking for å åpne den.</p>
      </CardContent>
    </Card>
  );
}
